import { browserStorage, type VisitorStorage } from "@/lib/visitor-storage";
import type { WidgetPrechatField, WidgetPrechatForm } from "@/lib/widget-config";

/** Map a prechat field kind to the matching `<input type>`. */
export function inputTypeFor(kind: WidgetPrechatField["kind"]): string {
  if (kind === "EMAIL") return "email";
  if (kind === "PHONE") return "tel";
  return "text";
}

/** Map a prechat field kind to a sensible `autocomplete` token. */
export function autocompleteFor(kind: WidgetPrechatField["kind"]): string | undefined {
  if (kind === "NAME") return "name";
  if (kind === "EMAIL") return "email";
  if (kind === "PHONE") return "tel";
  return "off";
}

/** localStorage key recording that this integration's prechat form was
 *  submitted (so we don't ask the visitor again on return visits). `scope`
 *  is the integration's `publicKey` — the stable per-integration id the
 *  widget already holds (the visitor config no longer carries an
 *  integrationId). */
export function prechatStorageKey(scope: string): string {
  return `wexio:prechat-submitted:${scope}`;
}

/** Whether the visitor already submitted the prechat form for this
 *  integration. Defensive against storage being unavailable (private
 *  mode / blocked cookies) — treats failure as "not submitted". */
export function hasSubmittedPrechat(
  scope: string,
  storage: VisitorStorage = browserStorage("local"),
): boolean {
  try {
    return storage.getItem(prechatStorageKey(scope)) === "1";
  } catch {
    return false;
  }
}

/** Record that this integration's prechat was satisfied so the fields
 *  don't reappear for the visitor. Swallows storage failures (private
 *  mode) — the dismissal is then in-memory for the session only. */
export function markPrechatSubmitted(
  scope: string,
  storage: VisitorStorage = browserStorage("local"),
): void {
  try {
    storage.setItem(prechatStorageKey(scope), "1");
  } catch {
    /* private mode — accept an in-memory dismissal for this session */
  }
}

/** Translated copy for the per-kind validation failures. */
export interface PrechatErrorMessages {
  required: string;
  invalidEmail: string;
  invalidPhone: string;
}

/**
 * Validate the prechat field values against the operator's form config.
 * Returns a `{ [fieldKey]: message }` map of the fields that failed —
 * empty when everything's valid. Mirrors the BE's own re-validation
 * (the BE is the source of truth; this is just fast client feedback).
 */
export function validatePrechat(
  form: WidgetPrechatForm,
  values: Record<string, string>,
  msgs: PrechatErrorMessages,
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const field of form.fields) {
    const value = (values[field.key] ?? "").trim();
    if (field.required && !value) {
      errors[field.key] = msgs.required;
      continue;
    }
    if (!value) continue;
    if (field.kind === "EMAIL" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      errors[field.key] = msgs.invalidEmail;
    } else if (field.kind === "PHONE" && !/^[+\d\s()-]{5,}$/.test(value)) {
      errors[field.key] = msgs.invalidPhone;
    }
  }
  return errors;
}

/** The populated (non-empty, trimmed) prechat fields, ready to POST. */
export function collectPrechatFields(
  form: WidgetPrechatForm,
  values: Record<string, string>,
): { key: string; value: string }[] {
  return form.fields
    .map((f) => ({ key: f.key, value: (values[f.key] ?? "").trim() }))
    .filter((f) => f.value.length > 0);
}

/**
 * Map host-supplied default profile values onto the form's field keys by
 * kind (`NAME → name`, `EMAIL → email`, `PHONE → phone`). `CUSTOM_TEXT`
 * fields have no source, so they're left for the visitor. Returns a
 * `{ [fieldKey]: value }` map (empty entries dropped) used to pre-fill
 * the prechat — and, when it covers every required field, to attach the
 * details + skip the form entirely.
 */
export function prefillToValues(
  form: WidgetPrechatForm,
  prefill: { name?: string; email?: string; phone?: string } | null | undefined,
): Record<string, string> {
  if (!prefill) return {};
  const out: Record<string, string> = {};
  for (const field of form.fields) {
    const raw =
      field.kind === "NAME"
        ? prefill.name
        : field.kind === "EMAIL"
          ? prefill.email
          : field.kind === "PHONE"
            ? prefill.phone
            : undefined;
    const value = raw?.trim();
    if (value) out[field.key] = value;
  }
  return out;
}
